import React, {
    useState,
    useRef,
    useLayoutEffect,
    useEffect,
} from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, Mousewheel } from "swiper/modules"
import "swiper/css"
import "swiper/css/autoplay"

import { MatchCard } from "./MatchCard"
import { cn } from "../lib/Utils"

export function SessionView({
                                session,
                                teamColors,
                                teams,
                                showHeader = true,
                            }) {
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" && window.innerWidth < 640
    )
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 640)
        window.addEventListener("resize", onResize)
        return () => window.removeEventListener("resize", onResize)
    }, [])
    const useSwiper = !isMobile

    const teamIds = Object.keys(teamColors)
    const colors = Object.values(teamColors)

    const wrapperRef = useRef(null)
    const matchCardRef = useRef(null)
    const swiperRef = useRef(null)

    const [maxWrapperHeight, setMaxWrapperHeight] = useState(0)
    const [cardHeight, setCardHeight] = useState(0)
    const [maxCount, setMaxCount] = useState(1)

    useLayoutEffect(() => {
        const SPACE = 10
        const measure = () => {
            if (!wrapperRef.current || !matchCardRef.current) return
            const wrapH = wrapperRef.current.clientHeight
            const cardH = matchCardRef.current.offsetHeight
            const count = Math.max(1, Math.floor(wrapH / cardH))
            setMaxWrapperHeight(wrapH)
            setCardHeight(cardH)
            setMaxCount(count)
            swiperRef.current?.update()
        }
        const raf = requestAnimationFrame(measure)
        window.addEventListener("resize", measure)
        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener("resize", measure)
        }
    }, [showHeader])

    const slideCount = Math.min(maxCount, Math.max(session.matches.length, 1) + 10)
    const neededHeight = slideCount * cardHeight + (slideCount - 1) * 10
    const wrapperHeightPx = Math.min(
        maxWrapperHeight || Infinity,
        neededHeight || maxWrapperHeight
    )

    useEffect(() => {
        const s = swiperRef.current
        if (!s) return
        if (session.matches.length >= 3) {
            s.autoplay.start()
        } else {
            s.autoplay.stop()
        }
    }, [session.matches.length])

    const resume = useRef(null)
    const onWheel = () => {
        const s = swiperRef.current
        if (!s) return
        s.params.autoplay.delay = 250
        s.autoplay.stop()
        s.autoplay.start()
        clearTimeout(resume.current)
        resume.current = setTimeout(() => {
            s.params.autoplay.delay = 10000
            s.autoplay.stop()
            s.autoplay.start()
        }, 5000)
    }
    useEffect(() => () => clearTimeout(resume.current), [])

    const wrapperStyle = { height: "100%" }

    return (
        <section className="mb-6 flex flex-col h-auto sm:h-screen lg:h-screen">
            {showHeader && (
                <div className="w-full max-w-screen-xl mx-auto px-4 my-2">
                    <div className="flex items-center justify-center">
                        <div className="flex-1 flex items-center justify-center min-w-0">
                            <div
                                className={cn(
                                    "round-point flex flex-col items-center justify-center",
                                    "w-12 h-10 text-base sm:w-16 sm:h-13 sm:text-lg md:w-24 md:h-15 md:text-3xl xl:w-32 xl:h-20",
                                    `team-header-${colors[0]}`
                                )}
                            >
                                <h2 className="mt-1 text-base sm:text-lg md:text-3xl xl:text-5xl font-bold truncate">
                                    {teams[teamIds[0]].score[session.id]}
                                </h2>
                            </div>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center px-2 text-center min-w-0">
                            <h3 className="text-base font-bold sm:text-xl md:text-2xl xl:text-4xl truncate">
                                {session.name}
                            </h3>
                            <p className="text-xs sm:text-sm md:text-base xl:text-xl text-gray-500 truncate">
                                {session.date}
                            </p>
                        </div>
                        <div className="flex-1 flex items-center justify-center min-w-0">
                            <div
                                className={cn(
                                    "round-point flex flex-col items-center justify-center",
                                    "w-12 h-10 text-base sm:w-16 sm:h-13 sm:text-lg md:w-24 md:h-15 md:text-3xl xl:w-32 xl:h-20",
                                    `team-header-${colors[1]}`
                                )}
                            >
                                <h2 className="mt-1 text-base sm:text-lg md:text-3xl xl:text-5xl font-bold truncate">
                                    {teams[teamIds[1]].score[session.id]}
                                </h2>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {useSwiper ? (
                <div
                    ref={wrapperRef}
                    className="flex-1 relative w-full 2xl:max-w-full px-4 sm:px-3 overflow-hidden"
                    onWheel={onWheel}
                    style={wrapperStyle}
                >
                    <Swiper
                        direction="vertical"
                        slidesPerView={slideCount}
                        spaceBetween={0}
                        loop={false}
                        grabCursor
                        modules={[Autoplay, Mousewheel]}
                        autoplay={{
                            delay: 10000,
                            disableOnInteraction: false,
                        }}
                        speed={3000}
                        style={{ height: "100%" }}
                        onBeforeInit={(s) => {
                            swiperRef.current = s
                        }}
                    >
                        {session.matches.map((match, idx) => (
                            <SwiperSlide
                                key={match.id}
                                style={{ paddingBottom: "10em" }}
                            >
                                <div
                                    ref={idx === 0 ? matchCardRef : null}
                                    className="w-full"
                                >
                                    <MatchCard
                                        className="w-full"
                                        match={match}
                                        matchIndex={idx}
                                        teamColors={teamColors}
                                        leftTeamId={teamIds[0]}
                                        rightTeamId={teamIds[1]}
                                        colors={colors}
                                    />
                                </div>
                            </SwiperSlide>
                        ))}

                        <SwiperSlide
                            key="ph"
                            style={{ paddingBottom: "10px" }}
                        >
                            <div className="w-full opacity-0 pointer-events-none">
                                <MatchCard
                                    className="w-full"
                                    match={session.matches[0]}
                                    matchIndex={0}
                                    teamColors={teamColors}
                                    leftTeamId={teamIds[0]}
                                    rightTeamId={teamIds[1]}
                                    colors={colors}
                                />
                            </div>
                        </SwiperSlide>
                    </Swiper>
                </div>
            ) : (
                <div className="flex-1 w-full overflow-auto">
                    <div className="flex flex-col">
                        {session.matches.map((match, idx) => (
                            <div
                                key={match.id}
                                style={{ paddingBottom: "10px" }}
                            >
                                <MatchCard
                                    className="w-full"
                                    match={match}
                                    matchIndex={idx}
                                    teamColors={teamColors}
                                    leftTeamId={teamIds[0]}
                                    rightTeamId={teamIds[1]}
                                    colors={colors}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    )
}
